/**
 * needsReview.js — what is waiting for a person, and when to say so
 * ==================================================================
 * Brian, 26 October 2026: *"SHFS says its missing some data and or trades that
 * need review. Where is that for me to review? Why isnt a pop up as soon as i
 * long in or something or an email?"*, and on 27 October: *"pop-up is fine"*
 * (D-278).
 *
 * The list comes from `GET /reports/needs-review/`, which the morning email and
 * the Dashboard's strip read too, so the three say the same thing. This file is
 * the logic: how the list groups by company, and when the pop-up opens.
 *
 * The pop-up opens after sign-in, again the next day while anything is still
 * waiting, and at once when something new joins the list. "Got it" puts it
 * away for the rest of the day. What was dismissed is a per-viewer
 * convenience in localStorage, wrapped, and cleared on sign out and sign in so
 * a new sign-in always shows the list.
 */

export const NEEDS_REVIEW_URL = "/reports/needs-review/";
// Fired after any answer on Approvals or the pop-up, so every reader of the
// list (the pop-up, the shell's count) reads it again.
export const NEEDS_REVIEW_CHANGED = "needs-review-changed";

/** How many things wait for a person: the number on the Approvals link. */
export function waitingCount(data) {
  const c = data?.counts || {};
  return (Number(c.broker_rows) || 0) + (Number(c.notices) || 0) + (Number(c.sent_again) || 0);
}
const KEY = "pms-needs-review-dismissed:v1";
export const NO_COMPANY = "Not tied to a deal";

/** Today in the viewer's own calendar, as YYYY-MM-DD. */
export function localDay(now = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * The keys that make the list what it is. A morning email the server cannot
 * send counts as an item, missing login or failed send, because one that never
 * arrives reads exactly like a quiet morning (CLAUDE.md §0.25).
 */
export function itemKeys(data) {
  const keys = (data?.items || []).map((i) => i.key);
  if (data?.email && data.email.configured === false) keys.push("email:not-configured");
  else if (data?.email?.reads_as) keys.push("email:not-sent");
  return keys;
}

export function readDismissed() {
  try {
    const entry = JSON.parse(window.localStorage.getItem(KEY) || "null");
    return entry && Array.isArray(entry.keys) && typeof entry.day === "string" ? entry : null;
  } catch {
    return null;
  }
}

export function saveDismissed(data, now = new Date()) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ day: localDay(now), keys: itemKeys(data) }));
  } catch {
    // Blocked storage: the pop-up comes back on the next load, which is the safe way to fail.
  }
}

export function clearDismissed() {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}

/** Should the pop-up open for this list, given what was dismissed and when? */
export function shouldOpen(data, dismissed, now = new Date()) {
  const keys = itemKeys(data);
  if (!keys.length) return false;
  if (!dismissed || dismissed.day !== localDay(now)) return true;
  const seen = new Set(dismissed.keys);
  return keys.some((k) => !seen.has(k));
}

/**
 * The items under their company, companies in the order of their oldest item,
 * rows not tied to a deal last. Within a company, oldest first.
 */
export function groupByCompany(items) {
  const groups = new Map();
  for (const item of items || []) {
    const name = item.company || NO_COMPANY;
    if (!groups.has(name)) groups.set(name, { company: name, dealId: null, items: [] });
    const group = groups.get(name);
    group.items.push(item);
    if (group.dealId === null && item.deal_id !== null && item.deal_id !== undefined) group.dealId = item.deal_id;
  }
  const oldest = (g) => g.items.reduce((min, i) => (i.date && i.date < min ? i.date : min), "9999-12-31");
  for (const g of groups.values()) g.items.sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  return [...groups.values()].sort((a, b) => {
    if (a.company === NO_COMPANY) return 1;
    if (b.company === NO_COMPANY) return -1;
    return oldest(a).localeCompare(oldest(b)) || a.company.localeCompare(b.company);
  });
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * One sentence for the top of the pop-up and the Dashboard: what kinds, how many.
 *
 * Brian, 27 October 2026: "broker trades should just post, only conversions and
 * other items should be flagged and required for approval" (D-281). A broker
 * row that needs nobody posts by itself, so what is counted here is a decision;
 * a sale waiting on a notice is counted with the notice, not again.
 */
export function summaryLine(counts) {
  const parts = [];
  if (counts?.notices) parts.push(plural(counts.notices, "notice to confirm or reject", "notices to confirm or reject"));
  if (counts?.sent_again) parts.push(plural(counts.sent_again, "notice sent more than once", "notices sent more than once"));
  if (counts?.broker_rows) parts.push(plural(counts.broker_rows, "broker row needing a decision", "broker rows needing a decision"));
  return parts.join(" · ");
}

export const BLOTTER_REVIEW_URL = (rowId, action) => `/integrations/blotter/${rowId}/${action}/`;
